/**
 * LoaderRing.tsx — the journey's one "something is happening" mark: a soft ring
 * with a green arc travelling round it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (CTO, 2026-09-24).
 *
 * WHY: the patient journey's waits used a pulsing logo tile (entry, hand-offs,
 * checkout) and a bare border spinner (the building beat). The CTO asked for
 * something calmer and more considered. This is shared by `BrandedLoader` and
 * the building checklist's CURRENT step, so both waits move the same way.
 *
 * The motion is two slow loops: the whole ring turns once every 1.8s while the
 * arc gently lengthens and shortens over 2.6s — it never flashes, blinks or
 * changes colour. Keyframes `gps-jux-orbit` / `gps-jux-arc` live in tokens.css
 * (journey-ux: loaders).
 *
 * ⚠️ THE TRANSFORM IS ON THE `<svg>` ITSELF, a leaf with no descendants but its
 * own shapes — never on a wrapper. A transformed ANCESTOR re-anchors any
 * `position: fixed` descendant (the Result screen's bar) and any non-portaled
 * `DialogShell`; a spinning leaf re-anchors nothing.
 *
 * TWO WEIGHTS (2026-09-24, Build B — loader + Result). `BrandedLoader` keeps
 * the default: a 4-unit brand-green arc on a mint track. The checking
 * checklist's CURRENT step passes `variant="thin"`: a 3-unit arc in the
 * journey's deep green (#0a4a2e, 10.34:1 on the white row it sits on) round a
 * warm hairline track — the quieter "thin turning ring" of the premium brief.
 * Same geometry, same keyframes, same leaf; only the paint differs.
 *
 * A THIRD, `halo` (2026-09-25 — the journey look of `BrandedLoader`): the
 * hairline ring drawn AROUND the logo seal on the first-load moment. Hairline
 * weights (a 0.8-unit arc round a 0.6-unit track — about 2.5px and 1.9px at the
 * 148px it is drawn at) and a SLOWER clock (a turn every 3.2s, the arc breathing
 * over 4.2s), because it frames the brand mark rather than standing in for it:
 * at the checklist's pace, a ring that size reads as a spinner. Still the same
 * geometry, keyframes and leaf.
 *
 * REDUCED MOTION: `reduced` comes from the caller's `usePrefersReducedMotion`
 * (the shared signal — never a second `matchMedia`). That cohort gets a STILL
 * ring with a fixed arc, and the global kill in tokens.css neutralises anything
 * left. Decorative in both cases: `aria-hidden`, no title — the words beside it
 * carry the meaning.
 */

/** Circumference of the r=20 ring in the fixed 48-unit viewBox (2πr ≈ 125.7). */
const RING_LENGTH = 126;

export function LoaderRing({
  size = 48,
  reduced,
  variant = 'default',
}: {
  size?: number;
  reduced: boolean;
  /** `thin` — the checklist's current step; `halo` — round the first-load seal (see the header). */
  variant?: 'default' | 'thin' | 'halo';
}) {
  const thin = variant === 'thin';
  const halo = variant === 'halo';
  // Unit weights in the fixed 48-unit viewBox (see ONE geometry, below).
  const trackWidth = halo ? 0.6 : thin ? 3 : 4;
  const arcWidth = halo ? 0.8 : thin ? 3 : 4;
  const quiet = thin || halo;
  // ONE geometry at every size — the viewBox is fixed and the ring scales, so
  // the arc keyframes (absolute dash lengths) are right at 24px and at 48px.
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-loader-ring={variant}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      style={{
        display: 'block',
        flex: 'none',
        animation: reduced ? undefined : `gps-jux-orbit ${halo ? '3.2s' : '1.8s'} linear infinite`,
      }}
    >
      <circle
        cx="24"
        cy="24"
        r="20"
        fill="none"
        stroke={quiet ? 'var(--gps-jux-loader-track, #e3dfd6)' : 'var(--gps-primary-tint-strong)'}
        strokeWidth={trackWidth}
      />
      <circle
        cx="24"
        cy="24"
        r="20"
        fill="none"
        stroke={quiet ? 'var(--gps-jux-loader-ink, #0a4a2e)' : 'var(--gps-primary)'}
        strokeWidth={arcWidth}
        strokeLinecap="round"
        // Starts at 12 o'clock; a still quarter-arc for the reduced cohort.
        transform="rotate(-90 24 24)"
        strokeDasharray={`32 ${RING_LENGTH}`}
        style={reduced ? undefined : { animation: `gps-jux-arc ${halo ? '4.2s' : '2.6s'} ease-in-out infinite` }}
      />
    </svg>
  );
}
